import { useCallback, useEffect, useState } from "react";
import type { PromptTemplateView } from "@seedance/contracts";
import { apiClient, type ApiClient } from "../../api/client.js";
import { canvasCopy } from "../../copy/zh-CN.js";

export type PromptTemplateApplyMode = "insert" | "replace";

export function applyPromptTemplate(input: {
  prompt: string;
  caret: number;
  content: string;
  mode: PromptTemplateApplyMode;
}): string {
  if (input.mode === "replace") return input.content;
  const caret = Math.max(0, Math.min(input.prompt.length, input.caret));
  return `${input.prompt.slice(0, caret)}${input.content}${input.prompt.slice(caret)}`;
}

export interface PromptTemplatePickerViewProps {
  status: "loading" | "ready" | "error";
  templates: PromptTemplateView[];
  selectedId: string | undefined;
  notice: string | undefined;
  onSelect(templateId: string): void;
  onApply(content: string, mode: PromptTemplateApplyMode): void;
  onClose(): void;
  onRetry(): void;
}

export function PromptTemplatePickerView(props: PromptTemplatePickerViewProps) {
  const selected = props.templates.find(({ id }) => id === props.selectedId);
  return (
    <aside className="prompt-template-picker" aria-label={canvasCopy.myPromptTemplates}>
      <header>
        <strong>{canvasCopy.myPromptTemplates}</strong>
        <button type="button" aria-label={canvasCopy.closePromptTemplates} onClick={props.onClose}>×</button>
      </header>
      {props.status === "loading" ? <p role="status">{canvasCopy.loadingPromptTemplates}</p> : null}
      {props.status === "error" ? (
        <p role="alert">
          {props.notice ?? canvasCopy.promptTemplatesLoadFailed}
          <button type="button" onClick={props.onRetry}>{canvasCopy.retry}</button>
        </p>
      ) : null}
      {props.status === "ready" && props.templates.length === 0 ? (
        <p>{canvasCopy.noPromptTemplates}</p>
      ) : null}
      {props.status === "ready" && props.templates.length > 0 ? (
        <ul>
          {props.templates.map((template) => (
            <li key={template.id}>
              <button
                type="button"
                aria-pressed={template.id === props.selectedId}
                onClick={() => props.onSelect(template.id)}
              >
                <strong>{template.title}</strong>
                <span>{template.content}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {selected ? (
        <div className="prompt-template-picker__actions">
          <button type="button" onClick={() => props.onApply(selected.content, "insert")}>
            {canvasCopy.insertPromptTemplate}
          </button>
          <button type="button" onClick={() => props.onApply(selected.content, "replace")}>
            {canvasCopy.replacePromptTemplate}
          </button>
        </div>
      ) : null}
    </aside>
  );
}

export function PromptTemplatePicker({
  open,
  onApply,
  onClose,
  client = apiClient
}: {
  open: boolean;
  onApply(content: string, mode: PromptTemplateApplyMode): void;
  onClose(): void;
  client?: ApiClient;
}) {
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [templates, setTemplates] = useState<PromptTemplateView[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const load = useCallback(async () => {
    if (!open) return;
    setStatus("loading");
    setNotice(undefined);
    try {
      const response = await client.listPromptTemplates("active");
      setTemplates(response);
      setSelectedId((current) =>
        current && response.some(({ id }) => id === current) ? current : undefined
      );
      setStatus("ready");
    } catch {
      setStatus("error");
      setNotice(canvasCopy.promptTemplatesLoadFailed);
    }
  }, [client, open]);
  useEffect(() => {
    void load();
  }, [load]);

  if (!open) return null;
  return (
    <PromptTemplatePickerView
      status={status}
      templates={templates}
      selectedId={selectedId}
      notice={notice}
      onSelect={setSelectedId}
      onApply={onApply}
      onClose={onClose}
      onRetry={() => void load()}
    />
  );
}
